import {
    DndContext as ToolkitDndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';

import { SortableOverlay } from '..';
import { GroupedAccountCardItem, SortableListProps } from '../../../../../../../../models/account';
import { arrayMove, isInDifferentContext, isOverDifferentItem } from '../../utils';
import { useDndContext } from '../DndContextProvider/DndContextProvider';

export function Dnd({ renderItem, children }: React.PropsWithChildren<SortableListProps>) {
    const {
        active: [active, setActive],
        grouped: [groupedItems, setGroupedItems],
    } = useDndContext();
    const flatted = groupedItems.reduce((acc, cur) => [...acc, ...cur.values], [] as GroupedAccountCardItem[]);
    const activeItem = useMemo(() => flatted.find((item) => item.id === active?.id), [flatted, active]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    return (
        <ToolkitDndContext
            sensors={sensors}
            onDragStart={({ active }) => {
                setActive(active);
            }}
            onDragEnd={(event) => {
                if (event.over && event.active.id !== event.over.id) {
                    const fromSource = event?.active?.data?.current?.sortable;
                    const fromId = fromSource.items[fromSource.index];
                    const toIndex = event?.over?.data?.current?.sortable.index;
                    const toGroupId = event.over?.data.current?.sortable.containerId ?? event.over.id;

                    const move = arrayMove(groupedItems, fromId, fromSource.index, toIndex, toGroupId);

                    setGroupedItems(move);
                }
                setActive(null);
            }}
            onDragCancel={() => {
                setActive(null);
            }}
            onDragOver={(event: DragOverEvent) => {
                const differentContext = isInDifferentContext(event);
                if (differentContext) {
                    if (isOverDifferentItem(event)) {
                        const fromSource = event?.active?.data?.current?.sortable;
                        const fromId = fromSource.items[fromSource.index];
                        const toIndex = event?.over?.data?.current?.sortable.index;
                        const toGroupId = event!.over!.data!.current!.sortable.containerId;

                        const move = arrayMove(groupedItems, fromId, fromSource.index, toIndex, toGroupId);

                        setGroupedItems(move);
                    } else {
                        // prepend to the new group
                        const fromSource = event?.active?.data?.current?.sortable;
                        const fromId = fromSource.items[fromSource.index];
                        const toGroupId = event.over?.id;

                        if (toGroupId) {
                            setGroupedItems(arrayMove(groupedItems, fromId, fromSource.index, 0, toGroupId));
                        }
                    }
                }
            }}
        >
            {children}
            {createPortal(
                <SortableOverlay>{activeItem ? renderItem(activeItem) : null}</SortableOverlay>,
                document.getElementsByClassName('edit-account-overview-container')[0],
            )}
        </ToolkitDndContext>
    );
}
